'use client';

import { ssrExchange, UrqlProvider } from '@urql/next';
import { getApps, initializeApp } from 'firebase/app';
import { Settings } from 'luxon';
import React, { FC, memo, useMemo, useRef } from 'react';
import { useEffectOnce } from 'react-use';

import AppInitializer from '@components/AppInitializer';
import { ColorModeProvider } from '@components/ui/color-mode';
import { Provider as ChakraUIProvider } from '@components/ui/provider';
import { Toaster } from '@components/ui/toaster';
import firebaseConfig from 'config/firebase';
import { makeUrqlClient } from 'helpers/urqlClient';
import { createRootStore, RootStoreProvider } from 'stores/rootStore';

interface Props {
  children: React.ReactNode;
  cookies?: string;
  authCookie?: string;
}

const AppShell: FC<Props> = ({ children, cookies, authCookie }) => {
  const [client, ssr] = useMemo(() => {
    const exchange = ssrExchange({
      isClient: typeof window !== 'undefined',
    });
    const getCookies = () => cookies;
    const urqlClient = makeUrqlClient({ ssr: exchange, getCookies, authCookie });
    return [urqlClient, exchange];
  }, [cookies, authCookie]);

  const rootStoreRef = useRef(createRootStore(client));

  useEffectOnce(() => {
    if (getApps().length === 0) {
      initializeApp(firebaseConfig);
    }
    Settings.defaultZone = 'utc';
  });

  return (
    <UrqlProvider client={client} ssr={ssr}>
      <ChakraUIProvider>
        <ColorModeProvider>
          <RootStoreProvider value={rootStoreRef.current}>
            <AppInitializer isAuthenticated={authCookie != null}>
              {children}
              <Toaster />
            </AppInitializer>
          </RootStoreProvider>
        </ColorModeProvider>
      </ChakraUIProvider>
    </UrqlProvider>
  );
};
AppShell.displayName = 'AppShell';

export default memo(AppShell);
